import {
  additionAmount,
  additionKindLabel,
  additionsTotal,
  advanceText,
  analyse,
  daysWithText,
  offerExpired,
  roDate,
  shortfall,
  shortfallText,
  specsText,
  todayIso,
  type OfferAddition,
  benchLine,
  benchTitle,
  costNotes,
  coveredText,
  daysAgo,
  daysText,
  extrasText,
  fmtNum,
  hasCostInfo,
  historyDiff,
  historyDiffShort,
  historyDiffTitle,
  judgePrice,
  lei,
  money,
  offerQuestions,
  packageChecks,
  perUnitMonth,
  priceCell,
  regimeMismatch,
  summary,
  termsOf,
  termsText,
  termsWarning,
  transportText,
  unitNotes,
  verdictNote,
  type AnalyseColumn,
  type Rfq,
  type RfqBench,
  t,
} from '@procumi/shared';
import { Avatar } from './Avatar';
import { useState } from 'react';

/** Cheia sub care se aliniază aceeași poziție suplimentară la furnizori diferiți. */
const addKey = (a: OfferAddition) =>
  `${a.desc.trim().toLocaleLowerCase('ro').replace(/\s+/g, ' ')}|${a.unit.trim().toLocaleLowerCase('ro')}`;

/**
 * Tabelul comparativ — diferențiatorul produsului.
 * Toate numerele vin din `analyse()` din @procumi/shared, acoperit de teste.
 */
export function ComparisonTable({
  rfq,
  onAsk,
  onSplit,
  bench = {},
}: {
  rfq: Rfq;
  /** ce spune istoricul firmei, pe poziție; lipsește până se încarcă */
  bench?: RfqBench;
  /** deschide mailul cu întrebările: pentru oferta care ar câștiga sau, cu `sendId`, pentru acel furnizor */
  onAsk?: (sendId?: string) => void;
  /** deschide împărțirea comenzii pe furnizori */
  onSplit?: () => void;
}) {
  const a = analyse(rfq);
  if (!a.cols.length) {
    return (
      <div className="empty">
        {t(
          'Nicio ofertă încă. Când un furnizor răspunde, apasă „Adaugă oferta” și prețurile apar aici, poziție cu poziție.',
        )}
      </div>
    );
  }
  const s = summary(a);
  const costs = hasCostInfo(a);
  const notes = [...costNotes(a), ...unitNotes(a, rfq.items)];
  const ask = onAsk ? offerQuestions(rfq) : null;
  const askSend = ask ? rfq.sends.find((x) => x.id === ask.sendId) : undefined;
  const hist = new Map(a.cols.map((c) => [c.send.id, historyDiff(rfq.items, c.send.offer!, bench)] as const));
  const anyHist = [...hist.values()].some((d) => d != null);
  const anyExtras = a.cols.some((c) => (c.send.offer!.extras ?? []).length > 0);
  /** Costul total; cu transport separat fără sumă, e doar o limită de jos. */
  const totalText = (c: AnalyseColumn) => `${lei(c.total)}${c.costUnknown ? t(' + transport necunoscut') : ''}`;
  /** Doar ofertele complete, pe toată cantitatea și cu costul știut, câștigă la total. */
  const isBestTotal = (c: AnalyseColumn) => a.full.includes(c) && !c.costUnknown && c.total === a.bestTotal;
  /** Totalurile nu se pot compara încă: cine are transportul necunoscut ar putea fi sub cel mai mic sigur. */
  const unsure = a.unsure.length > 0;
  const azi = todayIso();

  // Pozițiile puse în plus de furnizori, aliniate pe denumire: aceeași consolă
  // la doi furnizori stă pe același rând. Nu intră în costul total.
  const addRows = new Map<string, { label: OfferAddition; by: Map<string, OfferAddition> }>();
  for (const c of a.cols) {
    for (const add of c.send.offer!.additions ?? []) {
      const k = addKey(add);
      const row = addRows.get(k) ?? { label: add, by: new Map<string, OfferAddition>() };
      if (!row.by.has(c.send.id)) row.by.set(c.send.id, add);
      addRows.set(k, row);
    }
  }
  const anyAdditions = addRows.size > 0;
  const anyAdvance = a.cols.some((c) => c.send.offer!.advance);

  return (
    <>
      <div className="cmp-wrap">
        <table className="cmp">
          <thead>
            <tr>
              <th>{t('Poziție')}</th>
              {a.cols.map((c) => (
                <th key={c.send.id}>
                  <span className="sup-h">
                    <Avatar name={c.supplier.name} />
                    <span>
                      <b>{c.supplier.name}</b>
                      <span className="tot">
                        {c.complete && c.partial
                          ? t('{0} · ofertă parțială', totalText(c))
                          : c.complete
                            ? totalText(c)
                            : rfq.items.some(
                                  (it) =>
                                    c.send.offer!.prices[it.id] != null && regimeMismatch(it, c.send.offer!),
                                )
                              ? t('alt regim decât cel cerut')
                              : t('ofertă incompletă')}
                      </span>
                      {c.send.offer!.currency === 'EUR' && c.send.offer!.fxRate ? (
                        <span className="tot">{t('în euro · curs {0}', fmtNum(c.send.offer!.fxRate))}</span>
                      ) : null}
                    </span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rfq.items.map((it) => (
              <tr key={it.id}>
                <td>
                  {it.desc}
                  <span className="qty">
                    {fmtNum(it.qty)} {it.unit}
                    {it.rentDays != null ? t(' · chirie, {0}', daysText(it.rentDays)) : ''}
                    {specsText(it.specs) ? ` · ${specsText(it.specs)}` : ''}
                  </span>
                  {benchLine(bench[it.id], it.unit) ? (
                    <span className="bench" title={benchTitle(bench[it.id])}>
                      {benchLine(bench[it.id], it.unit)}
                    </span>
                  ) : null}
                </td>
                {a.cols.map((c) => {
                  const offer = c.send.offer!;
                  const pkg = offer.packages?.[it.id];
                  const off = offer.prices[it.id] != null ? regimeMismatch(it, offer) : null;
                  if (off) {
                    // Ofertă de alt regim: se vede, cu motivul, dar nu intră în comparație.
                    return (
                      <td key={c.send.id} className="off-cell">
                        {t('nu se compară')}
                        <span className="line">{off}</span>
                      </td>
                    );
                  }
                  const cell = priceCell(a, it, c);
                  if (cell && pkg) {
                    const perMonth = perUnitMonth(pkg);
                    const checks = packageChecks(pkg, it, offer.currency);
                    return (
                      <td key={c.send.id} className={cell.best ? 'best' : ''}>
                        <span className="pr">{cell.line}</span>
                        {cell.best ? <span className="best-tag">{t('cel mai mic')}</span> : null}
                        <span className="line">
                          {money(pkg.total, offer.currency)}
                          {pkg.periodDays ? ` / ${daysText(pkg.periodDays)}` : ' set'}
                          {pkg.oneTime ? t(' + {0} o dată', money(pkg.oneTime, offer.currency)) : ''}
                          {coveredText(pkg, it) ? ` · ${coveredText(pkg, it)}` : ''}
                        </span>
                        {perMonth != null ? (
                          <span className="line">
                            {t('{0}/{1}/lună', money(perMonth, offer.currency), it.unit)}
                          </span>
                        ) : null}
                        {checks.map((w) => (
                          <span key={w} className="cell-warn">
                            {w}
                          </span>
                        ))}
                        {offer.alts?.[it.id] ? (
                          <span className="cell-warn">
                            {t('Propune:')} {offer.alts[it.id]}
                          </span>
                        ) : null}
                      </td>
                    );
                  }
                  if (!cell) {
                    return (
                      <td key={c.send.id} className="muted">
                        —
                      </td>
                    );
                  }
                  // Comanda minimă: avertizare când te obligă la mai mult, altfel doar se vede.
                  const terms = termsOf(offer, it);
                  const warn = termsWarning(it, terms);
                  const note = verdictNote(judgePrice(offer.prices[it.id]!, bench[it.id]));
                  const short = shortfall(offer, it);
                  return (
                    <td key={c.send.id} className={cell.best ? 'best' : ''}>
                      <span className="pr">{cell.unit}</span>
                      <span className="line">{cell.line}</span>
                      {cell.best ? <span className="best-tag">{t('cel mai mic')}</span> : null}
                      {short ? (
                        <span className="cell-warn">
                          {t('Ofertă parțială: {0}', shortfallText(short, it.unit))}
                        </span>
                      ) : null}
                      {offer.alts?.[it.id] ? (
                        <span className="cell-warn">
                          {t('Propune:')} {offer.alts[it.id]}
                        </span>
                      ) : null}
                      {warn ? (
                        <span className="cell-warn">{warn}</span>
                      ) : terms ? (
                        <span className="line">{termsText(terms, it.unit)}</span>
                      ) : null}
                      {note ? (
                        <span className={`bench-note ${note.tone}`} title={benchTitle(bench[it.id])}>
                          {note.text}
                        </span>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
            {anyAdditions ? (
              <tr className="add-head">
                <td colSpan={a.cols.length + 1}>
                  {t('Poziții suplimentare ofertate')}
                  <span className="qty">
                    {t('puse de furnizor în plus față de cerere; nu intră în costul total')}
                  </span>
                </td>
              </tr>
            ) : null}
            {[...addRows.entries()].map(([k, row]) => (
              <tr key={k} className="add-row">
                <td>
                  {row.label.desc}
                  <span className="qty">
                    <span className="pill cat">
                      {t('suplimentar · {0}', additionKindLabel(row.label.kind))}
                    </span>
                  </span>
                </td>
                {a.cols.map((c) => {
                  const add = row.by.get(c.send.id);
                  if (!add) {
                    return (
                      <td key={c.send.id} className="muted">
                        —
                      </td>
                    );
                  }
                  const offer = c.send.offer!;
                  const v = additionAmount(add);
                  return (
                    <td key={c.send.id}>
                      {v != null ? money(v, offer.currency) : t('fără sumă')}
                      <span className="line">
                        {add.qty != null ? `${fmtNum(add.qty)} ${add.unit}` : ''}
                        {add.qty != null && add.unitPrice != null ? ' × ' : ''}
                        {add.unitPrice != null ? money(add.unitPrice, offer.currency) : ''}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            {costs ? (
              <>
                <tr>
                  <td>{t('Valoare poziții')}</td>
                  {a.cols.map((c) => (
                    <td key={c.send.id}>{c.complete ? lei(c.subtotal) : '—'}</td>
                  ))}
                </tr>
                <tr>
                  <td>{t('Transport')}</td>
                  {a.cols.map((c) => (
                    <td key={c.send.id}>
                      {c.costUnknown ? (
                        <span className="cell-warn cell-ask">
                          {t('transport necunoscut')}
                          {onAsk ? (
                            <button className="btn small" onClick={() => onAsk(c.send.id)}>
                              {t('Întreabă furnizorul')}
                            </button>
                          ) : null}
                        </span>
                      ) : (
                        transportText(c.send.offer!)
                      )}
                    </td>
                  ))}
                </tr>
                {anyExtras ? (
                  <tr>
                    <td>{t('Alte costuri')}</td>
                    {a.cols.map((c) => (
                      <td key={c.send.id}>
                        <FootText text={extrasText(c.send.offer!)} />
                      </td>
                    ))}
                  </tr>
                ) : null}
              </>
            ) : null}
            <tr className="total">
              <td>{costs ? t('Cost total fără TVA') : t('Total fără TVA')}</td>
              {a.cols.map((c) => (
                <td key={c.send.id} className={isBestTotal(c) ? 'best' : ''}>
                  <span className="pr">{c.complete ? totalText(c) : '—'}</span>
                  {isBestTotal(c) ? <span className="best-tag">{t('cea mai mică completă')}</span> : null}
                  {c.complete && c.partial ? (
                    <span className="cell-warn">{t('ofertă parțială: nu se compară la total')}</span>
                  ) : null}
                </td>
              ))}
            </tr>
            {anyHist ? (
              <tr className="hist">
                <td
                  title={t(
                    'Prețurile ofertei față de ce ai plătit ultima dată sau, unde n-ai comandat încă, față de media ta.',
                  )}
                >
                  {t('Față de istoric')}
                </td>
                {a.cols.map((c) => {
                  const d = hist.get(c.send.id);
                  if (!d) {
                    return (
                      <td key={c.send.id} className="muted">
                        —
                      </td>
                    );
                  }
                  const n = historyDiffShort(d);
                  return (
                    <td key={c.send.id} title={historyDiffTitle(d)}>
                      <span className={`bench-note ${n.tone}`}>{n.text}</span>
                      {d.covered < d.priced ? (
                        <span className="line">{t('pe {0} din {1} poziții', d.covered, d.priced)}</span>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ) : null}
            {anyAdditions ? (
              <tr>
                <td>{t('Poziții suplimentare')}</td>
                {a.cols.map((c) => {
                  const add = additionsTotal(c.send.offer!);
                  return (
                    <td key={c.send.id}>
                      {add.count ? lei(add.lei) : '—'}
                      {add.unknown ? <span className="line">{t('+ {0} fără sumă', add.unknown)}</span> : null}
                      {add.count ? <span className="line">{t('separat, în afara totalului')}</span> : null}
                    </td>
                  );
                })}
              </tr>
            ) : null}
            {anyAdvance ? (
              <tr>
                <td>{t('Avans')}</td>
                {a.cols.map((c) => (
                  <td key={c.send.id}>
                    <FootText text={advanceText(c.send.offer!.advance, c.send.offer!) || t('nu spune')} />
                  </td>
                ))}
              </tr>
            ) : null}
            <tr>
              <td>{t('Termen plată')}</td>
              {a.cols.map((c) => (
                <td key={c.send.id}>
                  <FootText text={daysWithText(c.send.offer!.paymentDays, c.send.offer!.payment)} />
                </td>
              ))}
            </tr>
            <tr>
              <td>{t('Termen livrare')}</td>
              {a.cols.map((c) => (
                <td key={c.send.id}>
                  <FootText text={daysWithText(c.send.offer!.deliveryDays, c.send.offer!.delivery)} />
                </td>
              ))}
            </tr>
            <tr>
              <td>{t('Valabilitate ofertă')}</td>
              {a.cols.map((c) => {
                const o = c.send.offer!;
                return (
                  <td key={c.send.id}>
                    {o.validUntil ? (
                      <span className={offerExpired(o, azi) ? 'cell-warn' : undefined}>
                        {offerExpired(o, azi)
                          ? t('expirată pe {0}', roDate(o.validUntil))
                          : t('până pe {0}', roDate(o.validUntil))}
                      </span>
                    ) : (
                      <FootText text={o.validity} />
                    )}
                  </td>
                );
              })}
            </tr>
            <tr>
              <td>{t('Observații')}</td>
              {a.cols.map((c) => (
                <td key={c.send.id}>
                  <FootText text={c.send.offer!.notes} />
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      {notes.length || askSend ? (
        <div className="cost-notes">
          {notes.map((n) => (
            <p key={n} className="read-warn">
              {n}
            </p>
          ))}
          {ask && askSend ? (
            <div className="read-note ask-note">
              <span>
                {t(
                  'Înainte să alegi, întreabă-l pe {0}: {1}.{2}',
                  askSend.supplier.name,
                  ask.questions.map((q) => q.topic).join(', '),
                  askSend.askedAt ? t(' Ai întrebat {0}.', daysAgo(askSend.askedAt)) : '',
                )}
              </span>
              <button className="btn small" onClick={() => onAsk?.()}>
                {t('Întreabă')}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {s ? (
        <div className="verdict">
          {unsure ? (
            // Fără suma transportului, nimeni nu e „cel mai ieftin”: spunem asta, nu un câștigător
            <div className="v-win v-unsure">
              <span>
                <span className="muted">{costs ? t('Cel mai mic cost total') : t('Cel mai mic total')}</span>
                <span className="big">{t('nu se știe încă')}</span>
                <span className="who">
                  {t(
                    'Totalurile nu sunt complete: {0} n-a dat suma transportului.',
                    a.unsure.map((c) => c.supplier.name).join(t(' și ')),
                  )}
                </span>
              </span>
            </div>
          ) : (
            <div className="v-win">
              <Avatar name={s.win.supplier.name} size="lg" />
              <span>
                <span className="muted">{costs ? t('Cel mai mic cost total') : t('Cel mai mic total')}</span>
                <span className="big">{totalText(s.win)}</span>
                <span className="who">{s.win.supplier.name}</span>
              </span>
            </div>
          )}
          {s.mix != null ? (
            <div>
              <span className="muted">{t('Dacă împarți comanda pe poziții')}</span>
              <span className="big">
                {lei(s.mix)}
                {a.mixUnknown ? t(' + transport necunoscut') : ''}
              </span>
              {/* un transport necunoscut în împărțire face din economie o presupunere */}
              {unsure || a.mixUnknown ? null : (
                <span className="save">{t('−{0} față de cel mai mic total', lei(s.saving!))}</span>
              )}
              {onSplit ? (
                <button className="btn small v-split" onClick={onSplit}>
                  {rfq.chosen?.split?.length ? t('Schimbă împărțirea') : t('Împarte comanda')}
                </button>
              ) : null}
            </div>
          ) : null}
          {s.spread != null && !unsure ? (
            <div>
              <span className="muted">{t('Diferență între oferte')}</span>
              <span className="big">{lei(s.spread)}</span>
              <span className="who">{t('între cea mai mică și cea mai mare completă')}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      <details className="cmp-help">
        <summary>{t('Cum se compară')}</summary>
        <p className="hint">
          {t(
            'Prețuri unitare în lei fără TVA; ofertele în euro sunt trecute în lei la cursul de sub furnizor. La chirie se compară costul pe toată durata cerută. Costul total adaugă transportul și costurile pe comandă; la comanda minimă se socotește cantitatea plătită. Pozițiile suplimentare puse de furnizor se arată separat și nu intră în costul total. O ofertă parțială (mai puțin decât s-a cerut) se vede, dar nu câștigă. Verde = cel mai bun preț pe poziție.',
          )}
        </p>
      </details>
    </>
  );
}

/**
 * Textul liber de sub tabel (observații, condiții), cu lățime limitată: o
 * observație lungă nu mai lățește coloana furnizorului. Peste trei rânduri se
 * strânge, cu „mai mult” pentru restul.
 */
function FootText({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  if (!text) return <>—</>;
  const long = text.length > 90;
  return (
    <div className="foot-text">
      <div className={long && !open ? 'clamped' : undefined}>{text}</div>
      {long ? (
        <button className="link" onClick={() => setOpen(!open)}>
          {open ? t('mai puțin') : t('mai mult')}
        </button>
      ) : null}
    </div>
  );
}
